import { create } from "zustand";
import { SpogCompany } from "../legacy/types/SpogCompany";
import { ApolloError } from "@apollo/client";
import { GraphQLClient } from "../utils/apollo";
import {
  COMPANY_VIEW_QUERY,
  COMPANY_SPEND_BY_COUNTRY_QUERY,
  COMPANY_ORG_TREE_QUERY,
} from "../legacy/queries/Companies";
import { COMPANY_INSTALLS_PRESENCE } from "../legacy/queries/Installs";
import { parseJSON } from "../utils/useLocalStorage";
import { CompanyTab } from "../pages/Company/types";
import {
  INSTALL_FILTERS_STORAGE_KEY,
  INTENT_FILTERS_STORAGE_KEY,
} from "./const";
import { COMPANY_SEARCH_CLIENT } from "../utils/consts";
import type { IntentSignal } from "../pages/Company/views/components/Intent/types";
import type { CachedTopic } from "@/types/intent";

export interface InstallFilters {
  countryAlpha2s: string[];
  productAttributeIds: number[];
  productIds: number[];
  vendorIds: number[];
  productCategoryIds: string[];
  includeCorporateDescendants: boolean;
  companyId?: string; // Track which company these filters belong to
}

export interface IntentFilters {
  topicIds: string[];
  topicsCache: CachedTopic[];
  buyersJourneyIds: string[];
  contextTypeIds: string[];
  locationCountryAlpha2s: string[];
  locationStateIds: string[];
  locationRegionNames: string[];
  includeCorporateDescendants: boolean;
  numberOfCadences: number; // weeks of data (default: 6)
  techTopicsOnly: boolean; // false = all topics, true = technology related only
  companyId?: string;
}

const getDefaultIntentFilters = (companyId?: string): IntentFilters => ({
  topicIds: [],
  topicsCache: [],
  buyersJourneyIds: [],
  contextTypeIds: [],
  locationCountryAlpha2s: [],
  locationStateIds: [],
  locationRegionNames: [],
  includeCorporateDescendants: false,
  numberOfCadences: 6,
  techTopicsOnly: false,
  companyId,
});

const getDefaultInstallFilters = (companyId?: string): InstallFilters => ({
  countryAlpha2s: [],
  productAttributeIds: [],
  productIds: [],
  vendorIds: [],
  productCategoryIds: [],
  includeCorporateDescendants: false,
  companyId,
});

const loadInstallFiltersFromStorage = (
  currentCompanyId?: string
): InstallFilters => {
  try {
    const stored = localStorage.getItem(INSTALL_FILTERS_STORAGE_KEY);
    if (!stored) return getDefaultInstallFilters(currentCompanyId);

    const parsed = parseJSON<InstallFilters>(stored);
    if (!parsed) return getDefaultInstallFilters(currentCompanyId);

    // If company has changed, clear filters and return defaults
    if (currentCompanyId && parsed.companyId !== currentCompanyId) {
      localStorage.removeItem(INSTALL_FILTERS_STORAGE_KEY);
      return getDefaultInstallFilters(currentCompanyId);
    }

    return { ...getDefaultInstallFilters(currentCompanyId), ...parsed };
  } catch (error) {
    console.warn("Error loading install filters from localStorage:", error);
    return getDefaultInstallFilters(currentCompanyId);
  }
};

const saveInstallFiltersToStorage = (filters: InstallFilters): void => {
  try {
    localStorage.setItem(INSTALL_FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch (error) {
    console.warn("Error saving install filters to localStorage:", error);
  }
};

const loadIntentFiltersFromStorage = (
  currentCompanyId?: string
): IntentFilters => {
  try {
    const stored = localStorage.getItem(INTENT_FILTERS_STORAGE_KEY);
    if (!stored) return getDefaultIntentFilters(currentCompanyId);

    const parsed = parseJSON<IntentFilters>(stored);
    if (!parsed) return getDefaultIntentFilters(currentCompanyId);

    // If company has changed, clear filters and return defaults
    if (currentCompanyId && parsed.companyId !== currentCompanyId) {
      localStorage.removeItem(INTENT_FILTERS_STORAGE_KEY);
      return getDefaultIntentFilters(currentCompanyId);
    }

    return { ...getDefaultIntentFilters(currentCompanyId), ...parsed };
  } catch (error) {
    console.warn("Error loading intent filters from localStorage:", error);
    return getDefaultIntentFilters(currentCompanyId);
  }
};

const saveIntentFiltersToStorage = (filters: IntentFilters): void => {
  try {
    localStorage.setItem(INTENT_FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch (error) {
    console.warn("Error saving intent filters to localStorage:", error);
  }
};

export interface NavigationState {
  companyIds: string[];
  currentCompanyId: string | null;
}

export interface CompanyPageState {
  company: SpogCompany | null;
  companyOrgTree: SpogCompany[];
  loadingStates: {
    company: boolean;
    orgTree: boolean;
    spendData: boolean;
    installData: boolean;
  };
  error: ApolloError | null;
  hasSpendData: boolean;
  hasInstallData: boolean;
  installFilters: InstallFilters;
  intentFilters: IntentFilters;
  intentSignalDetailsOpen: boolean;
  selectedIntentSignal: IntentSignal | null;
  organizationViewOpen: boolean;
  drawerMode: boolean;
  companyScores: SpogCompany["scores"];
  navigation: NavigationState;
  activeTab: CompanyTab;

  fetchCompany: (
    companyId: string,
    initialInstallFilters?: InstallFilters,
    initialIntentFilters?: IntentFilters
  ) => Promise<void>;
  setInstallFilters: (filters: InstallFilters) => void;
  resetInstallFilters: () => void;
  updateInstallFiltersForCompany: (companyId: string) => void;
  setIntentFilters: (
    filters: IntentFilters | ((prev: IntentFilters) => IntentFilters)
  ) => void;
  resetIntentFilters: () => void;
  updateIntentFiltersForCompany: (companyId: string) => void;
  setIntentSignalDetailsOpen: (open: boolean) => void;
  setSelectedIntentSignal: (signal: IntentSignal | null) => void;
  toggleOrganizationView: () => void;
  setOrganizationViewOpen: (open: boolean) => void;
  setDrawerMode: (drawerMode: boolean) => void;
  setCompanyScores: (scores: SpogCompany["scores"]) => void;
  resetCompanyData: () => void;
  setActiveTab: (tab: CompanyTab) => void;

  // Navigation methods
  setNavigationContext: (
    companyIds: string[],
    currentCompanyId: string
  ) => void;
  navigateToPrevious: () => void;
  navigateToNext: () => void;
}

const apolloClient = GraphQLClient();

export const useCompanyPageStore = create<CompanyPageState>((set, get) => ({
  company: null,
  companyOrgTree: [],
  error: null,
  hasSpendData: false,
  hasInstallData: false,
  installFilters: getDefaultInstallFilters(),
  intentFilters: getDefaultIntentFilters(),
  intentSignalDetailsOpen: false,
  selectedIntentSignal: null,
  organizationViewOpen: false,
  drawerMode: false,
  companyScores: undefined,
  navigation: {
    companyIds: [],
    currentCompanyId: null,
  },
  loadingStates: {
    company: false,
    orgTree: false,
    spendData: false,
    installData: false,
  },
  activeTab: CompanyTab.SUMMARY,

  fetchCompany: async (
    companyId,
    initialInstallFilters,
    initialIntentFilters
  ) => {
    set({
      error: null,
      hasSpendData: false,
      hasInstallData: false,
      loadingStates: {
        company: true,
        orgTree: true,
        spendData: true,
        installData: true,
      },
    });

    // Primary query - Company data (critical for page render)
    try {
      const result = await apolloClient.query({
        query: COMPANY_VIEW_QUERY,
        variables: { hgId: companyId },
        context: { clientName: COMPANY_SEARCH_CLIENT },
      });

      if (result.error) throw result.error;

      const company = result.data?.company?.data?.[0] || null;

      // Check if initialInstallFilters has non-empty values
      const hasInitialInstallFilters =
        initialInstallFilters &&
        Object.values(initialInstallFilters).some(
          (value) => Array.isArray(value) && value.length > 0
        );

      // Use provided install filters if they have values, otherwise load from storage
      const installFiltersForCompany = hasInitialInstallFilters
        ? { ...getDefaultInstallFilters(companyId), ...initialInstallFilters }
        : loadInstallFiltersFromStorage(companyId);

      // Check if initialIntentFilters has non-empty values
      const hasInitialIntentFilters =
        initialIntentFilters &&
        (initialIntentFilters.topicIds.length > 0 ||
          initialIntentFilters.buyersJourneyIds.length > 0 ||
          initialIntentFilters.contextTypeIds.length > 0 ||
          initialIntentFilters.locationCountryAlpha2s.length > 0 ||
          initialIntentFilters.locationStateIds.length > 0 ||
          initialIntentFilters.locationRegionNames.length > 0);

      // Use provided intent filters if they have values, otherwise load from storage
      const intentFiltersForCompany = hasInitialIntentFilters
        ? { ...getDefaultIntentFilters(companyId), ...initialIntentFilters }
        : loadIntentFiltersFromStorage(companyId);

      set({
        company,
        loadingStates: { ...get().loadingStates, company: false },
        installFilters: installFiltersForCompany,
        intentFilters: intentFiltersForCompany,
        intentSignalDetailsOpen: false,
        selectedIntentSignal: null,
      });
    } catch (err) {
      const apolloError =
        err instanceof ApolloError
          ? err
          : new ApolloError({ networkError: err as Error });
      set({
        company: null,
        error: apolloError,
        loadingStates: { ...get().loadingStates, company: false },
      });
      console.error("Error fetching company:", apolloError);
      return; // Exit early if primary query fails
    }

    // Secondary queries - Execute independently without blocking

    // Organization Tree Query
    const fetchOrgTree = async () => {
      try {
        const result = await apolloClient.query({
          query: COMPANY_ORG_TREE_QUERY,
          variables: { hgId: companyId },
          context: { clientName: COMPANY_SEARCH_CLIENT },
        });
        set({
          companyOrgTree: result.data?.search?.data || [],
          loadingStates: { ...get().loadingStates, orgTree: false },
        });
      } catch (err) {
        console.error("Error fetching organization tree:", err);
        set({
          loadingStates: { ...get().loadingStates, orgTree: false },
        });
      }
    };

    // Spend Data Availability Check
    const fetchSpendData = async () => {
      try {
        const result = await apolloClient.query({
          query: COMPANY_SPEND_BY_COUNTRY_QUERY,
          variables: {
            hgIdsIncludingCorporateRelatives: companyId,
            categoryParentIds: ["0"],
            per: 1,
          },
          context: { clientName: COMPANY_SEARCH_CLIENT },
        });
        set({
          hasSpendData: (result.data?.spend?.count ?? 0) > 0,
          loadingStates: { ...get().loadingStates, spendData: false },
        });
      } catch (err) {
        console.error("Error checking spend data:", err);
        set({
          hasSpendData: false,
          loadingStates: { ...get().loadingStates, spendData: false },
        });
      }
    };

    // Install Data Availability Check
    const fetchInstallData = async () => {
      try {
        const result = await apolloClient.query({
          query: COMPANY_INSTALLS_PRESENCE,
          variables: { hgId: companyId },
          context: { clientName: COMPANY_SEARCH_CLIENT },
        });
        set({
          hasInstallData: (result.data?.install?.count ?? 0) > 0,
          loadingStates: { ...get().loadingStates, installData: false },
        });
      } catch (err) {
        console.error("Error checking install data:", err);
        set({
          hasInstallData: false,
          loadingStates: { ...get().loadingStates, installData: false },
        });
      }
    };

    // Execute secondary queries in parallel for better performance
    await Promise.all([fetchSpendData(), fetchInstallData(), fetchOrgTree()]);
  },

  setInstallFilters: (filters) => {
    const { company } = get();
    const filtersWithCompanyId = {
      ...filters,
      companyId: company?.hgId,
    };
    saveInstallFiltersToStorage(filtersWithCompanyId);
    set({ installFilters: filtersWithCompanyId });
  },

  resetInstallFilters: () => {
    const { company } = get();
    const defaultFilters = getDefaultInstallFilters(company?.hgId);
    saveInstallFiltersToStorage(defaultFilters);
    set({ installFilters: defaultFilters });
  },

  updateInstallFiltersForCompany: (companyId) => {
    const filtersForCompany = loadInstallFiltersFromStorage(companyId);
    set({ installFilters: filtersForCompany });
  },

  setIntentFilters: (filtersOrUpdater) => {
    const { company, intentFilters: currentFilters } = get();
    const newFilters =
      typeof filtersOrUpdater === "function"
        ? filtersOrUpdater(currentFilters)
        : filtersOrUpdater;
    const filtersWithCompanyId = {
      ...newFilters,
      companyId: company?.hgId,
    };
    saveIntentFiltersToStorage(filtersWithCompanyId);
    set({ intentFilters: filtersWithCompanyId });
  },

  resetIntentFilters: () => {
    const { company } = get();
    const defaultFilters = getDefaultIntentFilters(company?.hgId);
    saveIntentFiltersToStorage(defaultFilters);
    set({ intentFilters: defaultFilters });
  },

  updateIntentFiltersForCompany: (companyId) => {
    const filtersForCompany = loadIntentFiltersFromStorage(companyId);
    set({ intentFilters: filtersForCompany });
  },

  setIntentSignalDetailsOpen: (open) => {
    set({ intentSignalDetailsOpen: open });
  },

  setSelectedIntentSignal: (signal) => {
    set({ selectedIntentSignal: signal });
  },

  toggleOrganizationView: () => {
    set((state) => ({ organizationViewOpen: !state.organizationViewOpen }));
  },

  setOrganizationViewOpen: (open) => {
    set({ organizationViewOpen: open });
  },

  setDrawerMode: (drawerMode) => {
    set({ drawerMode });
  },

  setCompanyScores: (scores) => {
    set({ companyScores: scores });
  },

  setActiveTab: (tab) => {
    set({ activeTab: tab });
  },

  resetCompanyData: () => {
    set({
      company: null,
      companyOrgTree: [],
      error: null,
      hasSpendData: false,
      hasInstallData: false,
      companyScores: undefined,
      organizationViewOpen: false,
      intentFilters: getDefaultIntentFilters(),
      intentSignalDetailsOpen: false,
      selectedIntentSignal: null,
      loadingStates: {
        company: true,
        orgTree: true,
        spendData: true,
        installData: true,
      },
      navigation: {
        companyIds: [],
        currentCompanyId: null,
      },
    });
  },

  // Navigation methods
  setNavigationContext: (companyIds, currentCompanyId) =>
    set({ navigation: { companyIds, currentCompanyId } }),

  navigateToPrevious: () => {
    const { companyIds, currentCompanyId } = get().navigation;
    const currentIndex = companyIds.indexOf(currentCompanyId!);

    if (currentIndex > 0) {
      const previousCompanyId = companyIds[currentIndex - 1];
      set((state) => ({
        navigation: {
          ...state.navigation,
          currentCompanyId: previousCompanyId,
        },
      }));
    }
  },

  navigateToNext: () => {
    const { companyIds, currentCompanyId } = get().navigation;
    const currentIndex = companyIds.indexOf(currentCompanyId!);

    if (currentIndex < companyIds.length - 1) {
      const nextCompanyId = companyIds[currentIndex + 1];
      set((state) => ({
        navigation: {
          ...state.navigation,
          currentCompanyId: nextCompanyId,
        },
      }));
    }
  },
}));

// Selectors
export const useHasPrevious = () =>
  useCompanyPageStore((state) => {
    const { companyIds, currentCompanyId } = state.navigation;
    const currentIndex = companyIds.indexOf(currentCompanyId!);
    return currentIndex > 0;
  });

export const useHasNext = () =>
  useCompanyPageStore((state) => {
    const { companyIds, currentCompanyId } = state.navigation;
    const currentIndex = companyIds.indexOf(currentCompanyId!);
    return currentIndex < companyIds.length - 1;
  });
